/**
 * NsectionB12Page
 * B.12.洩漏（A6.4-FORM-METH-002 Section 12. Leakage）
 * 三頁籤：12.1 洩漏排放源識別(富文本) / 12.2 洩漏避免或最小化(富文本) / 12.3 洩漏排放處理(FormulaWizard)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB12Service, { NsectionB12Detail, FormulaEntry, FormulaParameter } from '../services/nsectionB12Service';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import LatexHelper from '../components/LatexHelper';
import { useToast } from '../contexts/ToastContext';
import { parseLatexSymbols } from '../utils/latexEvaluator';
import formulaConfigService, { FormulaConfigItem } from '../services/formulaConfigService';
import '../styles/DataTable.css';

type WizardStage = 'equations' | 'parameters' | 'conservative';

const EMPTY_ENTRY = (): FormulaEntry => ({ id: crypto.randomUUID(), equation_note: '', equation: { latex: '' }, parameters: [], calculation_result: { value: null, unit: '' } });

function fmtNum(v: number | null | undefined, d = 5): string { if (v === null || v === undefined) return '—'; return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d }); }

function evaluateLatex(latex: string, params: FormulaParameter[]): number | null {
  try {
    let expr = latex.includes('=') ? latex.split(/(?<![<>!])=(?!=)/).slice(1).join('=') : latex;
    [...params].filter(p => p.annual_value !== null && p.symbol.trim()).sort((a, b) => b.symbol.length - a.symbol.length)
      .forEach(p => { expr = expr.replace(new RegExp(p.symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), `(${p.annual_value})`); });
    expr = expr.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1)/($2)').replace(/\\sqrt\{([^}]*)\}/g, 'Math.sqrt($1)')
      .replace(/\\(?:sum|prod|int)(?:_\{[^}]*\})?(?:\^\{[^}]*\})?/g, '').replace(/\\times/g, '*').replace(/\\div/g, '/').replace(/\\cdot/g, '*')
      .replace(/\\left\s*\(/g, '(').replace(/\\right\s*\)/g, ')').replace(/\^\{([^}]*)\}/g, '**($1)').replace(/\^([0-9]+)/g, '**$1')
      .replace(/\\[a-zA-Z]+/g, '').replace(/\{/g, '(').replace(/\}/g, ')').replace(/_/g, '');
    const result = new Function(`return (${expr})`)(); // eslint-disable-line
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result * 100000) / 100000;
  } catch { return null; }
}

const COMMON_UNITS = [
  { label: 'tCO₂e', latex: '\\text{tCO}_2\\text{e}' }, { label: 'MWh', latex: '\\text{MWh}' }, { label: 'GWh', latex: '\\text{GWh}' },
  { label: 'kWh', latex: '\\text{kWh}' }, { label: 'GJ', latex: '\\text{GJ}' }, { label: 'MJ', latex: '\\text{MJ}' },
  { label: 'kg', latex: '\\text{kg}' }, { label: 't', latex: '\\text{t}' }, { label: 'm³', latex: '\\text{m}^3' },
  { label: '%', latex: '\\%' }, { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' }, { label: 'tCO₂e/GJ', latex: '\\text{tCO}_2\\text{e/GJ}' },
];


interface UnitInputProps { value: string; onChange: (v: string) => void; disabled?: boolean; }
const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false }) => {
  const [sp, setSp] = useState(false);
  return (<div style={{ position: 'relative' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled} placeholder="LaTeX unit" style={{ flex: 1, padding: '4px 6px', fontFamily: 'monospace', fontSize: 12, border: '1px solid #d0d0d0', borderRadius: 4 }} />
      {!disabled && <button type="button" onClick={() => setSp(p => !p)} style={{ padding: '4px 7px', fontSize: 13, borderRadius: 5, border: '1px solid #d0d0d0', background: sp ? '#4A90E2' : '#fafafa', color: sp ? '#fff' : '#555', cursor: 'pointer', lineHeight: 1 }}>≡</button>}
    </div>
    {value.trim() && <div style={{ marginTop: 4, padding: '2px 6px', background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5, fontSize: 13, display: 'inline-block' }}>
      {(() => { try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; } })()}
    </div>}
    {sp && <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, marginTop: 4, background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 320 }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{COMMON_UNITS.map((u, i) => <button key={i} type="button" onClick={() => { onChange(u.latex); setSp(false); }} style={{ padding: '4px 10px', fontSize: 12, borderRadius: 5, border: '1px solid #e0e0e0', background: '#fafafa', cursor: 'pointer' }}><span style={{ color: '#888', fontSize: 11 }}>{u.label}</span></button>)}</div>
    </div>}
  </div>);
};

const NsectionB12Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB12Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(1);

  const [leakageSources, setLeakageSources] = useState('');
  const [leakageAvoidance, setLeakageAvoidance] = useState('');
  const [leakageCalculate, setLeakageCalculate] = useState<FormulaEntry[]>([]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditEntry, setWizardEditEntry] = useState<FormulaEntry | undefined>(undefined);
  const [wizardStage, setWizardStage] = useState<WizardStage>('equations');
  const [wizardEntry, setWizardEntry] = useState<FormulaEntry>(EMPTY_ENTRY());
  const [paraTypeCodes, setParaTypeCodes] = useState<SystemCode[]>([]);
  const [wizardFormulaConfigs, setWizardFormulaConfigs] = useState<FormulaConfigItem[]>([]);
  const [wizardFormulaId, setWizardFormulaId] = useState<number | ''>('');

  useEffect(() => { if (!hasInitialized.current && !permissionLoading) { validateSession('NsectionB12Page'); hasInitialized.current = true; } }, [permissionLoading]);
  useEffect(() => { getSystemCodes({ code_etype: 'equation parameters type', is_active: true }).then(c => setParaTypeCodes(c)).catch(() => {}); }, []);
  useEffect(() => {
    if (!wizardOpen) { setWizardFormulaId(''); return; }
    formulaConfigService.listActive().then(setWizardFormulaConfigs).catch(() => {});
  }, [wizardOpen]);

  useEffect(() => {
    if (!selectedProposalId) { setData(null); setLeakageSources(''); setLeakageAvoidance(''); setLeakageCalculate([]); return; }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const r = await nsectionB12Service.getByProposal(selectedProposalId);
        if (r) { setData(r); setLeakageSources(r.leakage_sources || ''); setLeakageAvoidance(r.leakage_avoidance || ''); setLeakageCalculate(r.leakage_calculate || []); }
        else { setData(null); setLeakageSources(''); setLeakageAvoidance(''); setLeakageCalculate([]); }
      } catch (err: any) { if (err.response?.status !== 404) setError(err.response?.data?.detail || (isZh ? '載入失敗' : 'Load failed')); }
      finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const d: any = activeTab === 1 ? { leakage_sources: leakageSources } : activeTab === 2 ? { leakage_avoidance: leakageAvoidance } : { leakage_calculate: leakageCalculate };
      const r = await nsectionB12Service.update(selectedProposalId, d);
      setData(r); toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) { toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed')); }
    finally { setLoading(false); }
  };

  const openWizard = (entry?: FormulaEntry) => {
    setWizardEditEntry(entry); setWizardEntry(entry ? { ...entry, parameters: entry.parameters.map(p => ({ ...p })) } : EMPTY_ENTRY());
    setWizardStage('equations'); setWizardOpen(true);
  };
  const updateParam = (idx: number, patch: Partial<FormulaParameter>) => {
    setWizardEntry(prev => {
      if (idx < 0 || idx >= prev.parameters.length) return prev;
      const params = [...prev.parameters];
      params[idx] = { ...params[idx], ...patch };
      return { ...prev, parameters: params };
    });
  };

  const removeParam = (idx: number) => {
    setWizardEntry(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== idx),
    }));
  };

  const handleWizardSave = () => {
    const f = [...leakageCalculate];
    if (wizardEditEntry) { const i = f.findIndex(x => x.id === wizardEditEntry.id); if (i >= 0) f[i] = wizardEntry; } else f.push(wizardEntry);
    setLeakageCalculate(f); setWizardOpen(false);
  };
  const deleteFormula = async (id: string) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定刪除？' : 'Delete?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setLeakageCalculate(leakageCalculate.filter(f => f.id !== id));
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b12', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('nsection_b12', 'update');

  const cs: React.CSSProperties = { width: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px' };
  const tb = (a: boolean): React.CSSProperties => ({ padding: '10px 20px', border: 'none', borderBottom: a ? '3px solid #4A90E2' : '3px solid transparent', background: a ? '#f0f7ff' : 'transparent', color: a ? '#4A90E2' : '#666', fontWeight: a ? 600 : 400, cursor: 'pointer', fontSize: 14, marginBottom: -2 });
  const saveBtn = () => canEdit ? <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 16 }}><button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>{loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}</button></div> : null;

  return (
    <div className="page-container">
      <div className="page-header"><FunctionPageHeader funcCode="nsection_b12" /></div>
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 16 }}>{isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}</div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 16 }}>
            <button onClick={() => setActiveTab(1)} style={tb(activeTab === 1)}>{isZh ? '12.1 洩漏排放源識別' : '12.1 Leakage sources'}</button>
            <button onClick={() => setActiveTab(2)} style={tb(activeTab === 2)}>{isZh ? '12.2 洩漏避免或最小化' : '12.2 Avoidance/minimization'}</button>
            <button onClick={() => setActiveTab(3)} style={tb(activeTab === 3)}>{isZh ? '12.3 洩漏排放處理' : '12.3 Addressing leakage'}</button>
          </div>

          {/* Tab 1: 洩漏排放源識別 */}
          {activeTab === 1 && (
            <div style={cs}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>{isZh ? '12.1 洩漏排放源識別' : '12.1 Identification of leakage emission sources'}</div>
              {canEdit ? <RichTextEditor value={leakageSources} onChange={setLeakageSources} placeholder={isZh ? '請說明洩漏排放源...' : 'Identify leakage sources...'} minHeight="250px" />
                : <div style={{ padding: 16, fontSize: 15, color: '#333', lineHeight: 1.8, minHeight: 250 }} dangerouslySetInnerHTML={{ __html: leakageSources || '-' }} />}
              {saveBtn()}
            </div>
          )}

          {/* Tab 2: 洩漏避免或最小化 */}
          {activeTab === 2 && (
            <div style={cs}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>{isZh ? '12.2 洩漏避免或最小化' : '12.2 Avoidance or minimization of leakage'}</div>
              {canEdit ? <RichTextEditor value={leakageAvoidance} onChange={setLeakageAvoidance} placeholder={isZh ? '請說明洩漏避免或最小化方式...' : 'Describe avoidance or minimization...'} minHeight="250px" />
                : <div style={{ padding: 16, fontSize: 15, color: '#333', lineHeight: 1.8, minHeight: 250 }} dangerouslySetInnerHTML={{ __html: leakageAvoidance || '-' }} />}
              {saveBtn()}
            </div>
          )}

          {/* Tab 3: 洩漏排放處理 FormulaWizard */}
          {activeTab === 3 && (
            <div style={cs}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>{isZh ? '12.3 洩漏排放處理' : '12.3 Addressing leakage emissions'}</div>
              {canEdit && <div style={{ marginBottom: 12, textAlign: 'right' }}><button className="btn-primary" onClick={() => openWizard()}>{t('common.create')}</button></div>}
              {leakageCalculate.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 14 }}>{isZh ? '尚無公式' : 'No formulas yet.'}</div>
              ) : (
                <div className="data-table-container"><table className="data-table"><thead className="table-header-dark-green"><tr>
                  <th style={{ width: '5%' }}>#</th><th style={{ width: '35%' }}>{isZh ? '公式' : 'Equation'}</th>
                  <th style={{ width: '35%' }}>{isZh ? '參數' : 'Parameters'}</th><th style={{ width: '15%' }}>{isZh ? '計算結果' : 'Result'}</th>
                  <th style={{ width: '10%' }}>{t('common.actions')}</th>
                </tr></thead><tbody>
                  {leakageCalculate.map((f, idx) => (
                    <tr key={f.id}>
                      <td>{idx + 1}</td>
                      <td>{f.equation.latex && (() => { try { return <BlockMath math={f.equation.latex} />; } catch { return <span style={{ color: '#c33' }}>Error</span>; } })()}</td>
                      <td>{f.parameters.map((p, pi) => <div key={pi} style={{ fontSize: 13, marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                        {(() => { try { return <InlineMath math={p.symbol} />; } catch { return <span>{p.symbol}</span>; } })()}
                        {p.annual_value !== null && <span>= {fmtNum(p.annual_value)}</span>}
                        {p.unit && (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()}
                      </div>)}</td>
                      <td style={{ fontWeight: 600 }}>{fmtNum(f.calculation_result?.value)}</td>
                      <td className="actions">{canEdit && <>
                        <button className="btn-edit" onClick={() => openWizard(f)} style={{ marginRight: 8 }}>{t('common.edit')}</button>
                        <button className="btn-delete" onClick={() => deleteFormula(f.id)}>{t('common.delete')}</button>
                      </>}</td>
                    </tr>
                  ))}
                </tbody></table></div>
              )}
              {saveBtn()}
            </div>
          )}
        </div>
      )}

      {/* FormulaWizard Modal */}
      {wizardOpen && (
        <div className="modal-overlay" onClick={() => setWizardOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', width: 1200 }}>
            <div className="modal-header">
              <h2>{wizardEditEntry ? (isZh ? '編輯公式' : 'Edit Formula') : (isZh ? '新增公式' : 'Add Formula')}</h2>
              <button className="modal-close" onClick={() => setWizardOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* 步驟列 */}
              <div style={{ display: 'flex', marginBottom: 24, borderBottom: '1px solid #e0e0e0', paddingBottom: 12 }}>
                {(['equations', 'parameters', 'conservative'] as WizardStage[]).map((s, i) => {
                  const labels = { equations: isZh ? '公式' : 'Equation', parameters: isZh ? '參數定義' : 'Parameters', conservative: isZh ? '保守估算' : 'Conservative' };
                  const ci = (['equations', 'parameters', 'conservative'] as WizardStage[]).indexOf(wizardStage);
                  return <div key={s} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', margin: '0 auto 4px', background: i < ci ? '#52c41a' : i === ci ? '#4A90E2' : '#e0e0e0', color: i <= ci ? '#fff' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>{i < ci ? '✓' : i + 1}</div>
                    <div style={{ fontSize: 12, color: i === ci ? '#4A90E2' : '#999', fontWeight: i === ci ? 600 : 400 }}>{labels[s]}</div>
                  </div>;
                })}
              </div>

              {/* Stage 1 */}
              {wizardStage === 'equations' && <div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{isZh ? '公式說明' : 'Equation Note'}</label>
                  <RichTextEditor value={wizardEntry.equation_note} onChange={v => setWizardEntry(p => ({ ...p, equation_note: v }))} placeholder={isZh ? '說明公式用途（可標註 ex ante / ex post）...' : 'Describe equation purpose...'} minHeight="80px" />
                </div>
                {/* 從公式庫選取 */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{isZh ? '從公式庫選取（選填）' : 'Select from Formula Library (optional)'}</label>
                  <select value={wizardFormulaId} onChange={e => {
                    const id = e.target.value ? Number(e.target.value) : '';
                    setWizardFormulaId(id);
                    if (!id) return;
                    const fc = wizardFormulaConfigs.find(f => f.id === id);
                    if (fc?.formula_latex) setWizardEntry(p => ({ ...p, equation: { latex: fc.formula_latex! } }));
                  }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14 }}>
                    <option value="">-- {isZh ? '手動輸入' : 'Enter manually'} --</option>
                    {wizardFormulaConfigs.map(f => <option key={f.id} value={f.id}>{f.formula_code}：{isZh ? f.name_zh : f.name_en}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontWeight: 600 }}>{isZh ? 'LaTeX 公式' : 'LaTeX Equation'}</label>
                  <LatexHelper isZh={isZh} onInsert={sn => setWizardEntry(p => ({ ...p, equation: { latex: p.equation.latex + sn } }))} />
                </div>
                <textarea value={wizardEntry.equation.latex} onChange={e => setWizardEntry(p => ({ ...p, equation: { latex: e.target.value } }))}
                  placeholder="e.g. LE_{y} = \sum_{i} (AD_{i,y}^{leak} \times EF_{i,y}^{leak})"
                  style={{ width: '100%', minHeight: 80, padding: '10px 12px', fontFamily: 'monospace', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
                {wizardEntry.equation.latex.trim() && <div style={{ marginTop: 12, padding: '12px 16px', background: '#f8f8f8', borderRadius: 6, border: '1px solid #e8e8e8' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{isZh ? '預覽' : 'Preview'}</div>
                  {(() => { try { return <BlockMath math={wizardEntry.equation.latex} />; } catch { return <span style={{ color: '#c33' }}>Error</span>; } })()}
                </div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                  <button className="btn-secondary" onClick={() => setWizardOpen(false)}>{t('common.cancel')}</button>
                  <button className="btn-primary" disabled={!wizardEntry.equation.latex.trim()} onClick={() => {
                    const parsed = parseLatexSymbols(wizardEntry.equation.latex);
                    const cfgParams = wizardFormulaId ? (wizardFormulaConfigs.find(f => f.id === wizardFormulaId)?.parameters ?? []) : [];
                    if (parsed.length > 0) setWizardEntry(p => { const ex = new Set(p.parameters.map(pp => pp.symbol)); return { ...p, parameters: [...p.parameters, ...parsed.filter(s => !ex.has(s)).map(s => { const cp = cfgParams.find(c => c.symbol === s); return { symbol: s, para_type: null, scenarios: cp ? (isZh ? [cp.name_zh, cp.description_zh] : [cp.name_en || cp.name_zh, cp.description_en || cp.description_zh]).filter(Boolean).join(' ') : '', unit: cp?.unit ?? '', annual_value: null }; })] }; });
                    setWizardStage('parameters');
                  }}>{isZh ? '進入參數定義 →' : 'Parameters →'}</button>
                </div>
              </div>}

              {/* Stage 2 */}
              {wizardStage === 'parameters' && <div>
                <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>{isZh ? '公式' : 'Equation'}</div>
                  {(() => { try { return <BlockMath math={wizardEntry.equation.latex} />; } catch { return null; } })()}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontWeight: 600 }}>{isZh ? '參數列表' : 'Parameters'}</label>
                  <button className="btn-secondary" onClick={() => setWizardEntry(p => ({ ...p, parameters: [...p.parameters, { symbol: '', para_type: null, scenarios: '', unit: '', annual_value: null }] }))} style={{ fontSize: 13 }}>+ {isZh ? '新增' : 'Add'}</button>
                </div>
                {wizardEntry.parameters.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                    <thead><tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '15%' }}>{isZh ? '符號' : 'Symbol'}</th>
                      <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '12%' }}>{isZh ? '類別' : 'Type'}</th>
                      <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '43%' }}>{isZh ? '說明' : 'Description'}</th>
                      <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '26%' }}>{isZh ? '單位' : 'Unit'}</th>
                      <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', width: '4%' }}></th>
                    </tr></thead>
                    <tbody>{wizardEntry.parameters.map((p, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                          <input value={p.symbol} onChange={e => updateParam(idx, { symbol: e.target.value })} style={{ width: '100%', border: 'none', outline: 'none', fontFamily: 'monospace', fontSize: 13 }} placeholder="LE_{i,y}" />
                          {p.symbol && (() => { try { return <div style={{ marginTop: 4 }}><InlineMath math={p.symbol} /></div>; } catch { return null; } })()}
                        </td>
                        <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                          <select value={p.para_type ?? ''} onChange={e => updateParam(idx, { para_type: e.target.value ? Number(e.target.value) : null })} style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, background: 'transparent' }}>
                            <option value="">{isZh ? '皆適用' : 'All'}</option>
                            {paraTypeCodes.map(c => <option key={c.id} value={c.id}>{isZh ? c.code_cname : c.code_ename}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', minWidth: 200 }}>
                          <RichTextEditor value={p.scenarios} onChange={html => updateParam(idx, { scenarios: html })} placeholder={isZh ? '說明...' : 'Description...'} minHeight="72px" toolbar="inline" />
                        </td>
                        <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}><UnitInput value={p.unit} onChange={v => updateParam(idx, { unit: v })} /></td>
                        <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                          <button type="button" onClick={() => removeParam(idx)} style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 16 }}>✕</button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <div style={{ padding: 12, color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6, marginBottom: 16 }}>{isZh ? '尚無參數' : 'No parameters'}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                  <button className="btn-secondary" onClick={() => setWizardStage('equations')}>← {isZh ? '回公式' : 'Back'}</button>
                  <button className="btn-primary" onClick={() => setWizardStage('conservative')}>{isZh ? '保守估算 →' : 'Conservative →'}</button>
                </div>
              </div>}

              {/* Stage 3 */}
              {wizardStage === 'conservative' && <div>
                <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>{isZh ? '公式' : 'Equation'}</div>
                  {(() => { try { return <BlockMath math={wizardEntry.equation.latex} />; } catch { return null; } })()}
                </div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>{isZh ? '保守估算值' : 'Conservative Estimates'}</label>
                {wizardEntry.parameters.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}><thead><tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '參數' : 'Param'}</th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '說明' : 'Desc'}</th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '單位' : 'Unit'}</th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '年度預估值' : 'Value'}</th>
                  </tr></thead><tbody>{wizardEntry.parameters.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0' }}>{(() => { try { return <InlineMath math={p.symbol} />; } catch { return <span>{p.symbol}</span>; } })()}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0', fontSize: 13, color: '#666' }} dangerouslySetInnerHTML={{ __html: p.scenarios || '—' }} />
                      <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0' }}>{p.unit ? (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })() : '—'}</td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                        <input type="number" step="0.01" value={p.annual_value ?? ''} onChange={e => updateParam(idx, { annual_value: e.target.value ? Number(e.target.value) : null })}
                          style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, textAlign: 'right' }} />
                      </td>
                    </tr>
                  ))}</tbody></table>
                ) : <div style={{ padding: 12, color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6, marginBottom: 16 }}>{isZh ? '尚無參數' : 'No params'}</div>}
                <div style={{ marginTop: 16, padding: '14px 16px', background: '#f8faff', borderRadius: 8, border: '1px solid #d0e4ff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{isZh ? '結果單位' : 'Unit'}</label>
                      <div style={{ width: 220 }}><UnitInput value={wizardEntry.calculation_result.unit} onChange={v => setWizardEntry(p => ({ ...p, calculation_result: { ...p.calculation_result, unit: v } }))} /></div>
                    </div>
                    <button onClick={() => { const v = evaluateLatex(wizardEntry.equation.latex, wizardEntry.parameters); if (v !== null) setWizardEntry(p => ({ ...p, calculation_result: { ...p.calculation_result, value: v } })); else toast.warning(isZh ? '無法計算' : 'Cannot calculate'); }}
                      style={{ padding: '7px 20px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>▶ {isZh ? '計算' : 'Calculate'}</button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                      <span style={{ fontSize: 13, color: '#888' }}>{isZh ? '結果' : 'Result'}</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: wizardEntry.calculation_result.value !== null ? '#1a1a1a' : '#ccc', minWidth: 80, textAlign: 'right' }}>{fmtNum(wizardEntry.calculation_result.value)}</span>
                      {wizardEntry.calculation_result.unit && <span style={{ fontSize: 13, color: '#555' }}>{(() => { try { return <InlineMath math={wizardEntry.calculation_result.unit} />; } catch { return <span>{wizardEntry.calculation_result.unit}</span>; } })()}</span>}
                    </div>
                  </div>
                  {wizardEntry.calculation_result.value !== null && <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a' }}>✓ {isZh ? '計算完成' : 'Done'}</div>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                  <button className="btn-secondary" onClick={() => setWizardStage('parameters')}>← {isZh ? '回參數定義' : 'Back'}</button>
                  <button className="btn-primary" onClick={handleWizardSave}>{isZh ? '儲存公式' : 'Save'}</button>
                </div>
              </div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB12Page;
